import { useCallback, useRef } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { SupabaseClient } from '@supabase/supabase-js'
import { promptDialog } from '@/components/Dialogs'
import SubmitOverlay from '@/components/SubmitOverlay'
import { updateWithVersion, isStaleWriteError, isWriteBlockedError } from '@/lib/optimistic'
import { withTimeout } from '@/lib/saveTimeout'
import { supabase } from '@/lib/supabase'
import { ValidationError } from '@/lib/errorReporter'
import { isManagerOrAbove } from '@/lib/roles'
import { tenantRoleOf } from '@/modules/farm/blockEditGate'
import { DELIVERY_SCREEN_DOMAIN } from '@/lib/packhouseErrorDomains'
import type { CurrentUser } from '@/lib/useCurrentUser'
import type { Delivery } from '@/types/database'

// "Not coming" on a WhatsApp pre-announcement (ui-flow F01 UF-15, job J13).
//
// A draft for a truck that never came — or one captured with + New instead of
// Confirm — had no way off the board: drafts are never listed as cards, so it
// sat above the search for the board's whole 90-day window. This soft-deletes
// the draft with a required reason. It lands in Config → Admin → Recycle bin
// like every other soft-deleted delivery, and is restored from there.
//
// NO NEW SERVER PATH. The write is the one the Confirm-merge already makes
// (Deliveries.tsx, "Retire the draft FIRST"): an UPDATE of deleted_at /
// deleted_by / deleted_reason, which
//   * deliveries_sensitive_col_gate admits for MANAGER / ADMIN / SUPER_ADMIN /
//     FARM_ADMIN / FARM_MANAGER of the ACTIVE tenant (mig 422/447's column
//     list; mig 20260724120000 rewrote its role read to
//     `CASE WHEN is_platform_admin() THEN 'ADMIN' ELSE current_tenant_role()
//     END` in place, which is why prod's body matches no file in git), and
//     refuses everyone else with 42501;
//   * deliveries_audit_trg snapshots (with update_reason) and
//     deliveries_bump_version bumps.
// The version pin is the "still a draft" check: confirming the draft is an
// UPDATE, so a draft confirmed (or merged, or deleted) since the board loaded
// no longer matches its version and nothing is written. A server-side
// `status = 'DRAFT'` filter backs it, so the write can only ever land on a
// draft; any 0-row result reads as NOT_COMING_STALE, never as a success.
//
// Managers only — the button follows the gate, read off the ACTIVE tenant's
// role (`tenant_role`, what current_tenant_role() returns), not `role`.
// tenantRoleOf owns that rule (incl. a pre-tenant_role snapshot's sticky
// SUPER_ADMIN, which RLS does not see).
export function mayMarkNotComing(me: Pick<CurrentUser, 'role' | 'tenant_role'> | null | undefined): boolean {
  return isManagerOrAbove(tenantRoleOf(me))
}

/** The soft-delete patch. The reason is what the Recycle bin shows. */
export function notComingPatch(userId: string, reason: string, nowIso: string) {
  const why = reason.trim()
  return {
    deleted_at:     nowIso,
    deleted_by:     userId,
    deleted_reason: `Not coming: ${why}`,
    update_reason:  `announcement cleared, not coming: ${why}`,
  }
}

export const NOT_COMING_STALE =
  'This announcement changed since the board loaded (it may just have been confirmed). Nothing was removed. Refresh the board and check it again.'

type Draft = Pick<Delivery, 'delivery_id' | 'grower' | 'variety' | 'version' | 'status'>

// `db` is the board's save client (useSaveDb). The session is read off the
// shared client, as every other board save does: the isolated client carries a
// bearer, not a session.
export function useDraftNotComing(db: Pick<SupabaseClient, 'from'>) {
  const qc = useQueryClient()
  const asking = useRef(false)

  const m = useMutation({
    mutationFn: async ({ draft, reason }: { draft: Draft; reason: string }) => {
      if (draft.status !== 'DRAFT') {
        throw new ValidationError('Only an announcement that has not arrived can be marked Not coming.')
      }
      const { data: { session } } = await supabase.auth.getSession()
      const userId = session?.user?.id
      if (!userId) throw new ValidationError('Not signed in. Sign in again and retry.')
      try {
        await withTimeout(updateWithVersion('deliveries',
          { col: 'delivery_id', value: draft.delivery_id },
          draft.version,
          notComingPatch(userId, reason, new Date().toISOString()),
          { select: 'delivery_id', db, match: { status: 'DRAFT' } },
        ), 'Deliveries: not coming')
      } catch (e) {
        // Version moved (confirmed / merged meanwhile), or the row is no
        // longer a DRAFT at the same version: nothing was written either way.
        // Refresh the board behind the refusal so it shows what happened.
        if (isStaleWriteError(e) || isWriteBlockedError(e)) {
          void qc.invalidateQueries({ queryKey: ['deliveries'] })
          throw new ValidationError(NOT_COMING_STALE)
        }
        throw e
      }
    },
  })

  // The reason is asked BEFORE mutate(): SubmitOverlay's 60s watchdog starts
  // at mutate(), so a dialog left open would read as a failed save.
  const ask = useCallback(async (draft: Draft) => {
    // Only a draft is ever offered the button; this stops a stale card from
    // even asking. (The version pin refuses it server-side anyway.)
    if (asking.current || draft.status !== 'DRAFT') return
    asking.current = true
    try {
      const reason = await promptDialog({
        title:       'Not coming?',
        message:     `${draft.grower} — ${draft.variety}. This takes the announcement off the board. An admin can bring it back from Config → Admin → Recycle bin.`,
        placeholder: 'e.g. truck went elsewhere, captured with + New',
        hint:        'Required — kept with the deleted announcement.',
        confirm:     'Not coming',
        tone:        'danger',
      })
      if (reason === null) return
      m.mutate({ draft, reason })
    } finally {
      asking.current = false
    }
  }, [m])

  // Mounted by the board OUTSIDE the announcement card: the card disappears on
  // the refetch, and an overlay inside it would unmount before its tick painted.
  // The board refreshes after the tick (onDismiss), not before.
  const overlay = (
    <SubmitOverlay
      isPending={m.isPending}
      isSuccess={m.isSuccess}
      isError  ={m.isError}
      error    ={m.error}
      pendingLabel="Removing…"
      successLabel="Announcement removed"
      domain   ={DELIVERY_SCREEN_DOMAIN}
      onDismiss={() => {
        const removed = m.isSuccess
        m.reset()
        if (removed) void qc.invalidateQueries({ queryKey: ['deliveries'] })
      }}
    />
  )

  return { ask, overlay, isPending: m.isPending }
}
